import React, { useState, useEffect, useRef, useContext } from 'react';
import { MapPin, Ban } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import CesiumContext from './../../context/CesiumContext';
import PointDrawer from '@/helper/PointDrawer'; // Ensure correct path
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

export type DrawMode = 'EDIT' | 'INSERT' | 'NONE' | 'CANCEL';

interface DamagePointToolProps {
	onDrawComplete?: (positions: any[], type: 'damage' | 'inspection') => void;
}

export default function DamagePointTool({ onDrawComplete }: DamagePointToolProps) {
	const { t } = useTranslation();
	const { viewer, drawMode, setDrawMode, setSelectedDamageEntity, setSelectedDamage } = useContext(
		CesiumContext
	) as {
		viewer: any;
		drawMode: DrawMode;
		setDrawMode: (mode: DrawMode) => void;
		setSelectedDamageEntity: (entity: any) => void;
		setSelectedDamage: (damage: any) => void;
	};

	const [active, setActive] = useState<boolean>(false);
	const drawerRef = useRef<any>(null);

	const { inspectionId, damageId } = useParams<{
		inspectionId?: string;
		damageId?: string;
	}>();

	const saveType = damageId ? 'damage' : inspectionId ? 'inspection' : null;

	const isDisabled = drawMode === 'EDIT' || (drawMode === 'INSERT' && !active);

	useEffect(() => {
		if (!viewer) return;
		if (active) startDrawing();
		else stopDrawing();

		return () => stopDrawing();
	}, [active, viewer]);

	useEffect(() => {
		if (drawMode === 'CANCEL' && active) setActive(false);
		if (drawMode === 'INSERT' && !active) stopDrawing();
	}, [drawMode]);

	const handleButtonClick = () => {
		if (!saveType) {
			toast.error(t('damages.draw.openInspectionRequiredPoi'));
			return;
		}
		if (active) setDrawMode('CANCEL');
		else setActive(true);
	};

	const startDrawing = () => {
		setDrawMode('INSERT');
		if (!drawerRef.current) {
			drawerRef.current = new PointDrawer(viewer, {
				// Use 'once: true' in PointDrawer options so it auto-stops listening after click
				once: true,
				callback: (result: any) => {
					console.log('Point Drawn:', result, 'Type:', saveType);
					if (onDrawComplete && saveType) {
						onDrawComplete(result, saveType);
					}

					// IMPORTANT: We turn off the button state, which triggers stopDrawing().
					// If onDrawComplete saves the data and re-renders the map with a permanent entity,
					// the temporary tool entity disappearing is fine.
					// setActive(false);
				},
			});
		}
		drawerRef.current.start();
	};

	const stopDrawing = () => {
		// If we are stopping, clear the tool's temporary entity.
		if (drawerRef.current) {
			// .clear() removes the entity.
			// If you want the point to stay visible purely via the tool,
			// you'd need to modify PointDrawer to not clear on stop,
			// but standard practice is: Tool Clears -> React State adds permanent Entity.
			drawerRef.current.clear();
			drawerRef.current = null;
		}

		if (active) {
			setDrawMode('NONE');
			setSelectedDamageEntity(null);
			setSelectedDamage(null);
		}
	};

	return (
		<button
			onClick={handleButtonClick}
			disabled={isDisabled}
			title={t('damages.draw.addPointOfInterest')}
			className={cn(
				'flex items-center gap-1.5 px-2 sm:px-3 h-8 rounded-md text-xs font-medium transition-all duration-200 whitespace-nowrap',
				active
					? 'bg-primary text-primary-foreground shadow-sm hover:opacity-90'
					: isDisabled
						? 'opacity-50 cursor-not-allowed text-foreground/40 bg-transparent'
						: 'text-foreground/80 hover:bg-accent hover:text-accent-foreground'
			)}
		>
			{active ? <Ban className="w-3.5 h-3.5 shrink-0" /> : <MapPin className="w-3.5 h-3.5 shrink-0" />}
			<span className="hidden sm:inline">{active ? t('common.actions.cancel') : t('damages.draw.poi')}</span>
		</button>
	);
}
